'use client';

import { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { GoPlus } from 'react-icons/go';
import { HiMiniMinus } from 'react-icons/hi2';
import { useQuery } from '@apollo/client';
import { motion, AnimatePresence } from 'framer-motion';
import { PROJECT_CATEGORIES_QUERY, PROJECTS_QUERY } from '@/queries/queries';
import Logo from '@/components/logo';
import ProjectCard from '@/components/common/ProjectCard';

interface Project {
  id: string;
  slug: string;
  name: string;
  year: string;
  short_description: string;
  media?: {
    desktop_media?: { url: string }[];
    mobile_media?: { url: string }[];
    enable_mux_video: any;
    mux_video?: any;
  };
  project_categories?: { slug: string }[];
}

export default function ProjectsPage() {
  const [gridCols, setGridCols] = useState<2 | 4>(4);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [start, setStart] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 16;
  const isFetchingMore = useRef(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const { data, loading, error, fetchMore } = useQuery(PROJECTS_QUERY, {
    variables: { limit: LIMIT, start: 0 },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: categoryData,
    loading: categoryLoading,
    error: categoryError,
  } = useQuery(PROJECT_CATEGORIES_QUERY);

  // Initial load
  useEffect(() => {
    if (data?.projects && !selectedCategory) {
      setProjects((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const uniqueNewProjects = data.projects.filter((p: Project) => !existingIds.has(p.id));
        return [...prev, ...uniqueNewProjects];
      });
      setStart(data.projects.length);
      setHasMore(data.projects.length >= LIMIT);
    }
  }, [data?.projects, selectedCategory]);

  const filteredProjects = useMemo(() => {
    if (!selectedCategory) return data?.projects || [];
    return (
      data?.projects?.filter((project: Project) =>
        project.project_categories?.some((cat) => cat.slug === selectedCategory)
      ) || []
    );
  }, [data?.projects, selectedCategory]);

  useEffect(() => {
    if (filteredProjects.length > 0 || !selectedCategory) {
      setProjects(filteredProjects);
      setStart(filteredProjects.length);
      setHasMore(!selectedCategory && filteredProjects.length >= LIMIT);
    }
  }, [filteredProjects, selectedCategory]);

  useLayoutEffect(() => {
    if (showFilters && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showFilters, categoryData]);

  // Infinite Scroll
  useEffect(() => {
    const handleScroll = () => {
      const SCROLL_THRESHOLD = 300;
      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.body.offsetHeight;

      if (
        scrollPosition >= bottomPosition - SCROLL_THRESHOLD &&
        !loading &&
        !isFetchingMore.current &&
        hasMore &&
        !selectedCategory
      ) {
        isFetchingMore.current = true;
        fetchMore({
          variables: { start, limit: LIMIT },
        }).then((res) => {
          const newProjects = res.data?.projects || [];

          // Remove duplicates
          setProjects((prev) => {
            const existingIds = new Set(prev.map((p) => p.id));
            const uniqueNewProjects = newProjects.filter((p: Project) => !existingIds.has(p.id));
            return [...prev, ...uniqueNewProjects];
          });

          if (newProjects.length < LIMIT) setHasMore(false);
          setStart((prev) => prev + newProjects.length);
          isFetchingMore.current = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [start, loading, fetchMore, hasMore, selectedCategory]);

  useEffect(() => {
    setStart(0);
    setHasMore(true);
  }, [selectedCategory]);

  return (
    <main className="min-h-screen pt-16 md:pt-5 w-full project-list">
      {/* Top Section */}
      <div className="hidden md:flex justify-around text-end md:text-center leading-none md:pt-0">
        <p className="text-[14px] lg:text-[16px] font-light leading-5 lg:leading-[22px] md:max-w-[220px] lg:max-w-[362px] indent-10 text-start md:ml-[16.5rem] lg:ml-[23rem]">
          Our homes are of exceptional build quality and superior craftsmanship. We design
          aspirational spaces that inspire and elevate the everyday.
        </p>
      </div>

      <section className="py-8 pt-0 md:pt-0">
        {/* Filter & Grid Toggle */}
        <div className="md:grid md:grid-cols-2 md:px-5 mb-0 md:mb-5 gap-2 text-sm items-end md:pt-[93px]">
          <div className="hidden md:flex items-center text-base font-light leading-none">
            <p>Grid View</p>
            <button
              className={`px-1 ps-2 ${gridCols === 4 ? 'text-[#9E703E]' : ''}`}
              onClick={() => setGridCols(4)}
            >
              [ 4 ]
            </button>
            <button
              className={`px-1 ${gridCols === 2 ? 'text-[#9E703E]' : ''}`}
              onClick={() => setGridCols(2)}
            >
              [ 2 ]
            </button>
          </div>
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-3 md:gap-0">
            <div className="flex flex-col md:flex-row lg:justify-end md:items-end gap-4 md:gap-0 w-full md:w-auto">
              <div className="relative flex items-start justify-between hidden">
                <div
                  className="flex w-full md:w-auto text-[16px] whitespace-nowrap justify-between flex-row border-b md:border-0 py-5 md:py-0 gap-2 px-4 md:px-0 border-[#2B332A]/40 font-light leading-none items-center cursor-pointer"
                  onClick={() => setShowFilters((prev) => !prev)}
                >
                  Filter Categories{' '}
                  <span className="flex">[ {showFilters ? <HiMiniMinus /> : <GoPlus />} ]</span>
                </div>
              </div>
            </div>
            <div className="hidden md:flex md:grid-cols-1 items-center text-base">
              <div className="md:flex items-center hidden ms-auto">
                <Logo width={44} height={59} fillColor="#2B332A" />
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter Animation */}
        <AnimatePresence initial={false}>
          <motion.div
            className="relative overflow-hidden cat-box"
            initial={{ height: 0, opacity: 0 }}
            animate={
              showFilters ? { height: contentHeight, opacity: 1 } : { height: 0, opacity: 0 }
            }
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <div ref={contentRef}>
              <motion.div
                className="overflow-hidden px-0 w-full flex justify-start md:justify-center top-0 ml-[2px]"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <motion.div className="p-0 w-full">
                  {categoryLoading ? (
                    <div className="text-sm">Loading categories...</div>
                  ) : categoryError ? (
                    <div className="text-sm text-red-500">Error loading categories.</div>
                  ) : (
                    <ul className="flex flex-col justify-center items-start w-full md:w-max">
                      <li
                        className={`cursor-pointer font-light border-b px-4 py-5 md:py-1 md:px-0 leading-none border-[#2B332A]/40 md:border-0 w-full text-base hover:text-[#9E703E] ${
                          selectedCategory === null ? 'text-[#9E703E]' : ''
                        }`}
                        onClick={() => setSelectedCategory(null)}
                      >
                        All
                      </li>
                      {categoryData?.projectCategories?.map((category: any) => (
                        <li
                          key={category.slug}
                          className={`cursor-pointer font-light border-b border-[#2B332A]/40 md:border-0 px-4 py-5 md:py-1 md:px-0 leading-none w-full text-base hover:text-[#9E703E] ${
                            selectedCategory === category.slug ? 'text-[#9E703E]' : ''
                          }`}
                          onClick={() => setSelectedCategory(category.slug)}
                        >
                          {category.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Projects Grid */}
        {error && <div className="text-center mt-5 text-red-500">Error loading projects.</div>}
        {loading && projects.length === 0 && (
          <div className="text-center py-6 text-gray-500">Loading projects...</div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={gridCols}
            className={`grid sm:gap-3 px-4 md:px-5 pt-10 md:pt-0 transition-all duration-500 ${
              gridCols === 2
                ? 'grid-cols-1 md:grid-cols-2'
                : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            {projects.map((project, index) => {
              const desktopImage = project.media?.desktop_media?.[0]?.url || '/default-desktop.jpg';
              const mobileImage = project.media?.mobile_media?.[0]?.url || '/default-mobile.jpg';

              return (
                <motion.div
                  key={project.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                  <ProjectCard
                    desktopImageSrc={desktopImage}
                    mobileImageSrc={mobileImage}
                    title={project.name}
                    baseLink={`/projects/${project.slug}`}
                    enableMuxVideo={project?.media?.enable_mux_video}
                    muxPlaybackId={project?.media?.mux_video?.playback_id}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {isFetchingMore.current && (
          <div className="text-center py-4 text-sm text-gray-500">Loading more projects...</div>
        )}
      </section>
    </main>
  );
}
